import React from 'react';
import { Shield, Lock, CreditCard, CheckCircle } from 'lucide-react';

// Hoisted to module level so React doesn't see a new component type on every
// TrustBadges render (avoids destroying their DOM subtree across re-renders).
const StripeLogo = () => (
  <svg className="h-8 w-auto" viewBox="0 0 60 25" xmlns="http://www.w3.org/2000/svg">
    <path d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a10.64 10.64 0 0 1-4.56 1.02c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.02 1.04-.06 1.56zm-3.94-3.24c0-1.14-.56-2.45-1.82-2.45-1.28 0-1.96 1.23-2.14 2.45h3.96z" fill="#635BFF"/>
    <path d="M40.95 5.22h4.6v16.06h-4.6V5.22zm-6.79 16.06h4.6V10.67h-4.6v10.61zm0-12.26h4.6V5.65h-4.6v3.37zm-6.39 12.26h-4.6v-5.97c0-1.33-.05-3.13-1.91-3.13-1.91 0-2.21 1.49-2.21 3.03v6.07h-4.6V10.67h4.42v1.45h.06c.61-1.16 2.11-2.38 4.35-2.38 4.65 0 5.49 3.06 5.49 7.03v4.51z" fill="#635BFF"/>
    <path d="M5.85 10.67v10.61H1.25V10.67h4.6zM3.55 5.22c1.47 0 2.67 1.2 2.67 2.67s-1.2 2.67-2.67 2.67-2.67-1.2-2.67-2.67 1.2-2.67 2.67-2.67z" fill="#635BFF"/>
  </svg>
);

const VisaLogo = () => (
  <svg className="h-6 w-auto" viewBox="0 0 48 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19.5 1L16.5 15H13L16 1H19.5Z" fill="#1A1F71"/>
    <path d="M32.5 1.5C31.5 1 30 0.5 28.5 0.5C24 0.5 21 3 21 6.5C21 9 23 10.5 24.5 11.5C26 12.5 26.5 13 26.5 14C26.5 15.5 25 16 23.5 16C21.5 16 20.5 15.5 19 15L18.5 14.5L18 18C19.5 18.5 21.5 19 24 19C29 19 32 16.5 32 12.5C32 10.5 30.5 9 28.5 7.5C27 6.5 26 6 26 5C26 4 27 3 29 3C30.5 3 31.5 3.5 32 3.5L32.5 1.5Z" fill="#1A1F71"/>
    <path d="M38 1H35C34 1 33 1.5 33 2.5L27 15H32L33 12H38L38.5 15H43L38 1ZM34 9L36 4L37 9H34Z" fill="#1A1F71"/>
    <path d="M12 1L7 10.5L6.5 8C6.5 8 5 3 1 1H0.5L0.5 1.5C2.5 2 4.5 3 6 4.5C7.5 6 8.5 8 9 9L12 1Z" fill="#1A1F71"/>
  </svg>
);

const MastercardLogo = () => (
  <svg className="h-6 w-auto" viewBox="0 0 32 20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="10" cy="10" r="10" fill="#EB001B"/>
    <circle cx="22" cy="10" r="10" fill="#F79E1B"/>
    <path d="M16 3C18 4.5 19.5 7 19.5 10C19.5 13 18 15.5 16 17C14 15.5 12.5 13 12.5 10C12.5 7 14 4.5 16 3Z" fill="#FF5F00"/>
  </svg>
);

/**
 * TrustBadges Component
 * Displays security and payment trust badges
 */
const TrustBadges = ({ variant = 'default', showSSL = true }) => {
  if (variant === 'compact') {
    return (
      <div className="flex items-center justify-center gap-4 py-3" data-testid="trust-badges-compact">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Lock className="h-4 w-4 text-green-600" />
          <span>Secure Checkout</span>
        </div>
        <div className="h-4 w-px bg-gray-300" />
        <div className="flex items-center gap-2">
          <VisaLogo />
          <MastercardLogo />
        </div>
      </div>
    );
  }

  if (variant === 'footer') {
    return (
      <div className="flex flex-col items-center gap-4" data-testid="trust-badges-footer">
        {showSSL && (
          <div className="flex items-center gap-2 px-4 py-2 bg-green-50 rounded-full border border-green-200">
            <Lock className="h-4 w-4 text-green-600" />
            <span className="text-sm font-medium text-green-700">SSL Secured</span>
          </div>
        )}
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">Secured by</span>
          <StripeLogo />
        </div>
        <div className="flex items-center gap-2">
          <VisaLogo />
          <MastercardLogo />
          <svg className="h-6 w-auto" viewBox="0 0 38 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect width="38" height="24" rx="4" fill="#016FD0"/>
            <path d="M19 17.5C20.5 16 21.5 14 21.5 12C21.5 10 20.5 8 19 6.5C17.5 8 16.5 10 16.5 12C16.5 14 17.5 16 19 17.5Z" fill="white"/>
            <text x="5" y="16" fill="white" fontSize="8" fontWeight="bold">AMEX</text>
          </svg>
        </div>
      </div>
    );
  }

  // Default variant - full badges for checkout page
  return (
    <div className="bg-gray-50 rounded-xl p-6 border border-gray-100" data-testid="trust-badges">
      <div className="text-center mb-4">
        <h4 className="font-semibold text-gray-900 mb-1">Safe & Secure Checkout</h4>
        <p className="text-sm text-gray-500">Your information is protected</p>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-6">
        {showSSL && (
          <div className="flex flex-col items-center p-3 bg-white rounded-lg border">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mb-2">
              <Lock className="h-5 w-5 text-green-600" />
            </div>
            <span className="text-xs font-medium text-gray-700">256-bit SSL</span>
          </div>
        )}
        <div className="flex flex-col items-center p-3 bg-white rounded-lg border">
          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mb-2">
            <Shield className="h-5 w-5 text-blue-600" />
          </div>
          <span className="text-xs font-medium text-gray-700">Secure Payments</span>
        </div>
        <div className="flex flex-col items-center p-3 bg-white rounded-lg border">
          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mb-2">
            <CreditCard className="h-5 w-5 text-purple-600" />
          </div>
          <span className="text-xs font-medium text-gray-700">PCI Compliant</span>
        </div>
        <div className="flex flex-col items-center p-3 bg-white rounded-lg border">
          <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center mb-2">
            <CheckCircle className="h-5 w-5 text-teal-600" />
          </div>
          <span className="text-xs font-medium text-gray-700">Instant Access</span>
        </div>
      </div>

      <div className="border-t pt-4">
        <p className="text-xs text-gray-500 text-center mb-3">Accepted Payment Methods</p>
        <div className="flex items-center justify-center gap-3">
          <VisaLogo />
          <MastercardLogo />
          <StripeLogo />
        </div>
      </div>
    </div>
  );
};

export default TrustBadges;
